import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import * as haptic from '@theme/haptic';

/** @typedef {import('./types.js').ProductVariant} ProductVariant */

/**
 * @typedef {Object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {HTMLElement} liveRegion
 *
 * @extends {Component<ProductFormRefs>}
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Reset abort controller in case of reconnect after morph
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    haptic.haptic();

    const button = this.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData(form);

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const errorMessage = response.description || response.message || Theme.translations.cart_error;
          this.#setLiveRegion(errorMessage);
          this.#showVisibleError(errorMessage);
          haptic.error();
          this.dispatchEvent(new CartErrorEvent(this.id || '', response.message, response.description, response.errors));
          return;
        }

        return fetch(`${Theme.routes.cart_url}.js`)
          .then((cartResponse) => cartResponse.json())
          .catch(() => null)
          .then((cart) => {
            const fallbackItemCount = Number(formData.get('quantity')) || 1;
            const itemCount = typeof cart?.item_count === 'number' ? cart.item_count : fallbackItemCount;

            this.#setLiveRegion(Theme.translations.added);
            haptic.confirm();

            this.dispatchEvent(
              new CartAddEvent(response, this.id || '', {
                source: 'product-form-component',
                productId: this.dataset.productId,
                itemCount,
                sections: response.sections,
              })
            );
          });
      })
      .catch((error) => {
        console.error(error);
        this.#setLiveRegion(Theme.translations.cart_error);
      })
      .finally(() => {
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    const variant = /** @type {ProductVariant | null} */ (event.detail?.resource);

    if (event.detail?.data?.productId && this.dataset.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variantIdInput = this.#variantIdInput;
    if (variantIdInput) variantIdInput.value = variant?.id ? String(variant.id) : '';

    const button = this.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = !variant?.available;
    }
  };

  /**
   * Resolves the hidden variant id input from the live DOM instead of `refs`.
   *
   * `refs` is refreshed by a MutationObserver, so it only catches up on the
   * next microtask. A colour swatch swap morphs a whole new `<form>` in (the
   * `{% form %}` id embeds the product id, so the node is replaced rather than
   * morphed) and then dispatches the variant update synchronously, so
   * `refs.variantId` still points at the detached input of the previous
   * product and the id written to it never reaches the cart.
   *
   * @returns {HTMLInputElement | null}
   */
  get #variantIdInput() {
    const input = this.querySelector('input[ref="variantId"]');
    if (input instanceof HTMLInputElement) return input;

    const ref = this.refs.variantId;
    return ref instanceof HTMLInputElement && ref.isConnected ? ref : null;
  }

  /**
   * @param {string | undefined} text
   */
  #setLiveRegion(text) {
    this.refs.liveRegion.textContent = text || '';

    window.setTimeout(() => {
      this.refs.liveRegion.textContent = '';
    }, 5000);
  }

  /**
   * @param {string} message
   */
  #showVisibleError(message) {
    // Remove any existing error
    this.querySelector('.product-form__error')?.remove();

    const error = document.createElement('p');
    error.className = 'product-form__error';
    error.setAttribute('role', 'alert');
    error.textContent = message;

    const button = this.querySelector('[ref="addToCartButton"]');
    if (button) {
      button.insertAdjacentElement('beforebegin', error);
    } else {
      this.appendChild(error);
    }

    window.setTimeout(() => {
      error.remove();
    }, 5000);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
